import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Typography,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// MUI does not ship a TikTok icon, so a lightweight inline SVG is used instead.
const TikTokIcon: React.FC<{ fontSize?: number }> = ({ fontSize = 28 }) => (
  <svg
    width={fontSize}
    height={fontSize}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.6 5.82c-1.06-.93-1.7-2.28-1.7-3.82h-3.09v13.44a3.16 3.16 0 1 1-2.24-3.02V9.28a6.24 6.24 0 1 0 5.33 6.16V9.7a8.31 8.31 0 0 0 4.9 1.58V8.2c-1.2 0-2.32-.42-3.2-1.24z" />
  </svg>
);

interface ShareCalendarButtonProps {
  url: string;
  /** Button label text (defaults to the booking calendar share text). */
  label?: string;
  /** Dialog title text (defaults to the booking calendar share text). */
  dialogTitle?: string;
  /** Text used in the share message (defaults to 'Calendar Link'). */
  text?: string;
}

const ShareCalendarButton: React.FC<ShareCalendarButtonProps> = ({
  url,
  label = 'Share Calendar Link',
  dialogTitle = 'Share Booking Calendar',
  text = 'Calendar Link',
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const openShareWindow = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy calendar link:', error);
    }
  };

  const handleWhatsAppShare = () => {
    openShareWindow(`https://wa.me/?text=${encodeURIComponent(url)}`);
  };

  const handleFacebookShare = () => {
    openShareWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    );
  };

  // Instagram and TikTok have no public web intent for sharing an external
  // link, so the link is copied to the clipboard and the platform is opened
  // for the user to paste it (e.g. into a bio or story).
  const handleInstagramShare = async () => {
    void handleCopy();
    openShareWindow('https://www.instagram.com/');
  };

  const handleTikTokShare = async () => {
    void handleCopy();
    openShareWindow('https://www.tiktok.com/upload');
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ShareIcon />}
        onClick={() => setOpen(true)}
        sx={{ color: 'white', borderColor: 'white' }}
      >
        {label}
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 1 }}>
            <TextField
              label={text}
              value={url}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton
                aria-label="Copy calendar link"
                onClick={handleCopy}
                color={copied ? 'success' : 'default'}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Share via
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <Tooltip title="Share on WhatsApp">
              <IconButton
                aria-label="Share on WhatsApp"
                onClick={handleWhatsAppShare}
                sx={{ color: '#25D366' }}
              >
                <WhatsAppIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on Facebook">
              <IconButton
                aria-label="Share on Facebook"
                onClick={handleFacebookShare}
                sx={{ color: '#1877F2' }}
              >
                <FacebookIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy link and open Instagram">
              <IconButton
                aria-label="Share on Instagram"
                onClick={handleInstagramShare}
                sx={{ color: '#E4405F' }}
              >
                <InstagramIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy link and open TikTok">
              <IconButton
                aria-label="Share on TikTok"
                onClick={handleTikTokShare}
                sx={{ color: '#000000' }}
              >
                <TikTokIcon fontSize={28} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
      />
    </>
  );
};

export default ShareCalendarButton;
